import React from 'react';
import { Card } from '../components/ui/Card';
import { Link } from 'react-router-dom';
import './Pages.css';

interface Feature {
  id: string;
  name: string;
  description: string;
  icon: string;
  route?: string;
  status: 'available' | 'coming_soon' | 'beta';
  category: 'core' | 'advanced' | 'integration' | 'analytics';
}

const features: Feature[] = [
  // Core
  { id: 'dashboard', name: 'Dashboard', description: 'Visão geral dos pacientes, consultas e alertas', icon: '📊', route: '/dashboard', status: 'available', category: 'core' },
  { id: 'patients', name: 'Gerenciamento de Pacientes', description: 'Cadastro, histórico e acompanhamento completo', icon: '👥', route: '/pacientes', status: 'available', category: 'core' },
  { id: 'assessment', name: 'Avaliação Nutricional', description: 'Anamnese, exames e avaliação completa do paciente', icon: '📋', route: '/avaliacao', status: 'available', category: 'core' },
  { id: 'nutrition-calculation', name: 'Cálculo Nutricional', description: 'Protocolos de cálculo (TMB, GET, macronutrientes)', icon: '🧮', route: '/calculo-nutricional', status: 'available', category: 'core' },
  { id: 'meal-plans', name: 'Planos de Refeição', description: 'Criação e gerenciamento de planos alimentares', icon: '🍽️', route: '/planos', status: 'available', category: 'core' },
  { id: 'schedule', name: 'Agenda', description: 'Agendamento de consultas com integração Google Calendar', icon: '📅', route: '/agenda', status: 'available', category: 'core' },
  { id: 'measurements', name: 'Medições Antropométricas', description: 'Registro e acompanhamento de medidas corporais', icon: '📏', route: '/medicoes', status: 'available', category: 'core' },
  { id: 'chat', name: 'Chat', description: 'Comunicação direta com pacientes', icon: '💬', route: '/chat', status: 'available', category: 'core' },
  { id: 'reports', name: 'Relatórios', description: 'Relatórios e análises de progresso', icon: '📈', route: '/relatorios', status: 'available', category: 'core' },
  
  // Advanced
  { id: 'ai-meal-plans', name: 'Planos Alimentares com IA', description: 'Geração automática de planos usando inteligência artificial', icon: '🤖', status: 'available', category: 'advanced' },
  { id: 'exercise-plans', name: 'Planos de Exercícios', description: 'Criação de planos de treino personalizados', icon: '💪', status: 'available', category: 'advanced' },
  { id: 'diet-tracking', name: 'Rastreamento de Dieta', description: 'Acompanhamento diário de alimentação do paciente', icon: '📱', status: 'available', category: 'advanced' },
  { id: 'activity-tracking', name: 'Rastreamento de Atividades', description: 'Monitoramento de atividades físicas e exercícios', icon: '🏃', status: 'available', category: 'advanced' },
  { id: 'smartwatch-integration', name: 'Integração Smartwatch', description: 'Sincronização com dispositivos wearables', icon: '⌚', status: 'available', category: 'advanced' },
  { id: 'biometric-auth', name: 'Autenticação Biométrica', description: 'Login seguro com biometria no mobile', icon: '🔐', status: 'available', category: 'advanced' },
  
  // Integration
  { id: 'google-calendar', name: 'Google Calendar', description: 'Sincronização de consultas com Google Agenda', icon: '📅', status: 'available', category: 'integration' },
  { id: 'notifications', name: 'Sistema de Notificações', description: 'Alertas configuráveis para consultas e lembretes', icon: '🔔', status: 'available', category: 'integration' },
  
  // Analytics
  { id: 'progress-charts', name: 'Gráficos de Progresso', description: 'Visualização de evolução do paciente', icon: '📊', status: 'available', category: 'analytics' },
  { id: 'comparison-tools', name: 'Ferramentas de Comparação', description: 'Comparação entre medições e análises temporais', icon: '⚖️', status: 'available', category: 'analytics' },
  { id: 'bioimpedance-analysis', name: 'Análise de Bioimpedância', description: 'Análise de composição corporal', icon: '⚡', status: 'available', category: 'analytics' },
  { id: 'skinfold-analysis', name: 'Análise de Dobras Cutâneas', description: 'Avaliação de gordura corporal por dobras', icon: '📐', status: 'available', category: 'analytics' },
  
  // Coming Soon
  { id: 'video-call', name: 'Videochamada', description: 'Consultas online por videoconferência', icon: '📹', status: 'coming_soon', category: 'advanced' },
  { id: 'genetic-area', name: 'Área Genética', description: 'Análise genética e nutrigenômica', icon: '🧬', status: 'coming_soon', category: 'advanced' },
  { id: 'gestational', name: 'Acompanhamento Gestacional', description: 'Acompanhamento nutricional na gravidez', icon: '🤰', status: 'coming_soon', category: 'advanced' },
  { id: 'manipulated', name: 'Manipulado', description: 'Gestão de fórmulas manipuladas', icon: '💊', route: '/manipulados', status: 'available', category: 'core' },
  { id: 'exams', name: 'Exames', description: 'Gestão de exames laboratoriais', icon: '🔬', route: '/pedido-exames', status: 'available', category: 'core' },
  { id: 'pre-consultation', name: 'Pré-Consulta', description: 'Formulários pré-consulta automatizados', icon: '📝', route: '/pre-consulta', status: 'available', category: 'core' },
  { id: 'food-diary', name: 'Diário Alimentar', description: 'Registro detalhado de alimentação', icon: '📔', route: '/diario-alimentar', status: 'available', category: 'core' },
  { id: 'nutrition-instructions', name: 'Instruções Nutricionais', description: 'Biblioteca de orientações nutricionais', icon: '📚', route: '/instrucoes-nutricionais', status: 'available', category: 'core' },
];

const categories = {
  core: { label: 'Funcionalidades Principais', icon: '⭐' },
  advanced: { label: 'Funcionalidades Avançadas', icon: '🚀' },
  integration: { label: 'Integrações', icon: '🔗' },
  analytics: { label: 'Análises e Relatórios', icon: '📊' },
};

export const Resources: React.FC = () => {
  const getStatusBadge = (status: Feature['status']) => {
    switch (status) {
      case 'available':
        return <span style={{ fontSize: '10px', padding: '2px 6px', background: '#d1fae5', color: '#065f46', borderRadius: '4px', fontWeight: 600 }}>Disponível</span>;
      case 'beta':
        return <span style={{ fontSize: '10px', padding: '2px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: '4px', fontWeight: 600 }}>Beta</span>;
      case 'coming_soon':
        return <span style={{ fontSize: '10px', padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontWeight: 600 }}>Em breve</span>;
      default:
        return null;
    }
  };

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, Feature[]>);

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Recursos e Funcionalidades</h2>
          <p>Explore todas as funcionalidades disponíveis no NutriSync.</p>
        </div>
      </div>

      {Object.entries(categories).map(([categoryKey, categoryInfo]) => {
        const categoryFeatures = groupedFeatures[categoryKey] || [];
        if (categoryFeatures.length === 0) return null;

        return (
          <div key={categoryKey} style={{ marginBottom: '24px' }}>
            <Card
              title={`${categoryInfo.icon} ${categoryInfo.label}`}
              subtitle={`${categoryFeatures.length} funcionalidade(s) nesta categoria`}
            >
            <div className="ns-features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
              {categoryFeatures.map((feature) => {
                const content = (
                  <div
                    style={{
                      padding: '16px',
                      background: feature.status === 'available' ? 'var(--page-surface)' : 'var(--page-surface-hover)',
                      border: `1px solid ${feature.status === 'available' ? '#e2e8f0' : '#cbd5e1'}`,
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      cursor: feature.route && feature.status === 'available' ? 'pointer' : 'default',
                      opacity: feature.status === 'coming_soon' ? 0.7 : 1,
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                    }}
                    className={`ns-feature-card ${feature.route && feature.status === 'available' ? 'ns-feature-card-hover' : ''}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px', minWidth: 0 }}>
                      <span style={{ fontSize: '32px', lineHeight: 1, flexShrink: 0 }}>{feature.icon}</span>
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '8px', minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--page-text-primary)', wordWrap: 'break-word', overflowWrap: 'break-word', flex: 1, minWidth: 0 }}>{feature.name}</h3>
                          <span style={{ flexShrink: 0 }}>{getStatusBadge(feature.status)}</span>
                        </div>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--page-text-muted)', lineHeight: 1.5, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                    {feature.route && feature.status === 'available' && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <Link
                          to={feature.route}
                          style={{
                            fontSize: '12px',
                            color: 'var(--theme-link)',
                            textDecoration: 'none',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          Acessar →
                        </Link>
                      </div>
                    )}
                  </div>
                );

                return feature.route && feature.status === 'available' ? (
                  <Link key={feature.id} to={feature.route} style={{ textDecoration: 'none', color: 'inherit' }}>
                    {content}
                  </Link>
                ) : (
                  <div key={feature.id}>{content}</div>
                );
              })}
            </div>
          </Card>
          </div>
        );
      })}

      <div style={{ marginTop: '24px' }}>
        <Card
          title="💡 Dica"
          subtitle="Funcionalidades em desenvolvimento"
        >
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--page-text-muted)', lineHeight: 1.6 }}>
          Algumas funcionalidades estão marcadas como "Em breve" e serão disponibilizadas em atualizações futuras. 
          Fique atento às novidades!
        </p>
      </Card>
      </div>
    </div>
  );
};

